import React from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  alpha,
  useTheme,
  SvgIconProps
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EmojiEvents as AwardIcon,
  Groups as GroupsIcon,
  Paid as PriceIcon,
  SupportAgent as SupportAgentIcon,
  WorkspacePremium as SuccessIcon
} from '@mui/icons-material';

interface TrustItemProps {
  icon: React.ReactElement<SvgIconProps>;
  title: string;
  description: string;
}

const TrustItem: React.FC<TrustItemProps> = ({ icon, title, description }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: '16px',
        background: `linear-gradient(145deg, ${alpha('#1a1a1a', 0.4)} 0%, ${alpha('#0a0a0a', 0.4)} 100%)`,
        border: '1px solid rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center !important',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 2.5,
          p: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: '50%',
          width: 70,
          height: 70,
        }}
      >
        {React.cloneElement(icon, {
          color: "primary",
          sx: { fontSize: 35 },
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="h3" 
        align="center"
        sx={{ 
          fontWeight: 600,
          mb: 1.5,
          width: '100%',
          textAlign: 'center !important',
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        color="text.secondary"
        align="center"
        sx={{ 
          width: '100%',
          mx: 'auto',
          flexGrow: 1,
          textAlign: 'center !important',
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const TrustSection: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const trustItems = [
    {
      icon: <SuccessIcon />,
      title: t('trust.case.title'),
      description: t('trust.case.description'),
    },
    {
      icon: <GroupsIcon />,
      title: t('trust.team.title'),
      description: t('trust.team.description'),
    },
    {
      icon: <PriceIcon />,
      title: t('trust.price.title'),
      description: t('trust.price.description'),
    },
    {
      icon: <SupportAgentIcon />,
      title: t('trust.support.title'),
      description: t('trust.support.description'),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'radial-gradient(circle at top right, rgba(255,128,128,0.05) 0%, transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
            maxWidth: '800px',
            width: '100%',
            mx: 'auto'
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2
            }}
          >
            {t('trust.title')}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ 
              maxWidth: '650px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.125rem' },
              px: { xs: 1, md: 0 },
            }}
          >
            {t('trust.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', mx: 'auto' }}>
          <Grid container spacing={4} justifyContent="center">
            {trustItems.map((item, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <TrustItem
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default TrustSection; 